import {
  type GridKey,
  type GridView,
  useDeleteGridView,
  useSaveGridView,
  useSetDefaultGridView,
  useUpdateGridView
} from "@appretio/shared";
import { type Dispatch, type SetStateAction, useCallback } from "react";

/** Which view a picker is showing. */
export type ActiveView<K extends string = string> =
  | { kind: "none" }
  | { kind: "builtIn"; key: K }
  | { kind: "saved"; viewId: string };

/** How a picker's writes read the state to store. */
export interface GridViewCapture<TSections> {
  /** The state a new view stores, carrying ``sections``. */
  forSave: (sections: TSections) => string;
  /** The state ``view`` stores when its setup is refreshed from the screen. */
  forRefresh: (view: GridView) => string;
}

/**
 * The writes behind a picker's housekeeping: save, rename or refresh,
 * delete, promote to default. The Views and Context pickers share it; only
 * how the state is captured differs.
 */
export function useGridViewWrites<TSections, K extends string>(
  grid: GridKey,
  { forSave, forRefresh }: GridViewCapture<TSections>,
  activeSavedView: GridView | undefined,
  setActiveView: Dispatch<SetStateAction<ActiveView<K>>>
) {
  const saveMutation = useSaveGridView(grid);
  const updateMutation = useUpdateGridView(grid);
  const deleteMutation = useDeleteGridView(grid);
  const setDefaultMutation = useSetDefaultGridView(grid);

  const saveCurrentView = useCallback(
    async (name: string, sections: TSections, isDefault: boolean) => {
      const saved = await saveMutation.mutateAsync({
        name,
        control_state: forSave(sections),
        is_default: isDefault
      });
      if (saved) setActiveView({ kind: "saved", viewId: saved.view_id });
      return saved;
    },
    [forSave, saveMutation, setActiveView]
  );

  /**
   * Rename the applied view, optionally refreshing its stored state; leaving
   * the refresh off resends the stored string, and only the name moves.
   */
  const editActiveView = useCallback(
    async (name: string, replaceState: boolean) => {
      if (!activeSavedView) return undefined;
      const updated = await updateMutation.mutateAsync({
        view_id: activeSavedView.view_id,
        name,
        control_state: replaceState
          ? forRefresh(activeSavedView)
          : activeSavedView.control_state
      });
      if (updated) setActiveView({ kind: "saved", viewId: updated.view_id });
      return updated;
    },
    [activeSavedView, forRefresh, updateMutation, setActiveView]
  );

  const deleteView = useCallback(
    async (viewId: string) => {
      await deleteMutation.mutateAsync(viewId);
      setActiveView((current) =>
        current.kind === "saved" && current.viewId === viewId
          ? { kind: "none" }
          : current
      );
    },
    [deleteMutation, setActiveView]
  );

  // For a setup that lands from outside the picker, such as a feedback
  // restore: the applied view no longer describes it.
  const clearActiveView = useCallback(
    () => setActiveView({ kind: "none" }),
    [setActiveView]
  );

  return {
    saveCurrentView,
    editActiveView,
    deleteView,
    clearActiveView,
    setDefaultView: setDefaultMutation.mutate,
    isSaving: saveMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSettingDefault: setDefaultMutation.isPending
  };
}

/** The writes a picker's housekeeping icons need. */
export type GridViewHousekeeping = Pick<
  ReturnType<typeof useGridViewWrites>,
  | "editActiveView"
  | "deleteView"
  | "setDefaultView"
  | "isSaving"
  | "isDeleting"
  | "isSettingDefault"
>;
